import { BaseModelKeys } from '../../../models/base.model';
import { AgentKeys } from '../../../models/domain/agent.keys';
import type { GetListResponse } from './common';

/**
 * One projected sub-document: an `Address`, an `EmailAddress` or a `PhoneNumber`.
 *
 * Typed structurally rather than as those model classes on purpose. This file is compiled
 * into the Node API as well as the Angular apps, and those three modules reach for
 * `@elastic/elasticsearch` types and `uuid` — dependencies a server contract has no
 * business pulling in. The sub-documents are passed through key for key, and the client
 * hands them to consumers as the model types they already are.
 */
export type PlatformAgentChildDocument = Record<string, unknown>;

/**
 * Computed by the projection rather than carried by an agent document, which is why it is
 * not an `AgentKeys` value — the same reason the two resolved agency names below are not.
 */
export enum PlatformAgentDerivedKeys {
  hasFcmTokens = 'hasFcmTokens',
}

/**
 * The two names the Elasticsearch index produced with painless runtime fields, resolved
 * by JOIN now. They are not `AgentKeys`: no agent document has ever carried them, and the
 * grids that render them bind these literal names.
 */
export enum PlatformAgentResolvedNameKeys {
  agencyName = 'agency_name',
  mgaName = 'mga_name',
}

/**
 * An agent as the SQL projection serves it — the same document Elasticsearch served.
 *
 * Keyed by `AgentKeys` and `BaseModelKeys` rather than by camelCase API names, and
 * deliberately so: roughly thirty grids, lookups, pipes and modals already bind these
 * names, and the DevExtreme filters they send arrive as these names too. Emitting them
 * directly is what lets the backend change without touching a single consumer.
 *
 * The field set is `ElasticAgentIndexedFields` plus the three nested arrays and the two
 * resolved names, because that is exactly what the index carried. It is a projection of
 * the Firestore agent document, not the whole of it: anything the index never held is
 * still read from Firestore by `AgentService`.
 */
export type PlatformAgentRow = {
  /** Firestore document id. Every consumer keys its store on this. */
  [BaseModelKeys.dbId]: string;
  [AgentKeys.p_agent_id]: string | null;
  [AgentKeys.uid]: string | null;
  /** Carrier-side id, shown as a column on the portal's agency roster. */
  [AgentKeys.p_external_agent_id]: string | null;
  [AgentKeys.p_email]: string | null;
  [AgentKeys.p_agent_first_name]: string | null;
  [AgentKeys.p_agent_last_name]: string | null;
  /**
   * The remaining name parts, none of which the index carried.
   *
   * Served for the screens moving off the Firestore document — the agent header, the PWA
   * profile and the map's bottom sheet compose a displayed name from these. `p_prefix`
   * and `p_suffix` are lookup ids resolved through their pipes, not literal text.
   */
  [AgentKeys.p_agent_middle_name]: string | null;
  [AgentKeys.p_prefix]: string | null;
  [AgentKeys.p_suffix]: string | null;
  [AgentKeys.title]: string | null;
  /**
   * `First Last (email)`, composed server-side to the letter of `getAgentFullName`.
   *
   * The index had no such field — the client added it to every row it returned — so
   * sorting and filtering by display name could not be pushed down. Computing it in SQL
   * makes it an ordinary column, and the client stops rewriting rows it receives.
   */
  [AgentKeys.p_agent_name]: string;
  [AgentKeys.p_agency_id]: string | null;
  [AgentKeys.p_mga_id]: string | null;
  [PlatformAgentResolvedNameKeys.agencyName]: string | null;
  [PlatformAgentResolvedNameKeys.mgaName]: string | null;
  [AgentKeys.agent_status]: string | null;
  [AgentKeys.agent_type]: string | null;
  [AgentKeys.prospect_status]: string | null;
  [AgentKeys.registration_status]: string | null;
  [AgentKeys.approve_deny_reason]: string | null;
  /** Business id of the manager — the hierarchy edge, not a document id. */
  [AgentKeys.manager_id]: string | null;
  [AgentKeys.is_manager]: boolean | null;
  [AgentKeys.is_rmd]: boolean | null;
  /**
   * Never indexed, so the hierarchy tree-list column bound to it has always rendered
   * blank. The projection carries the column, so it is served.
   */
  [AgentKeys.is_credited]: boolean | null;
  [AgentKeys.p_registered_user]: boolean | null;
  [AgentKeys.christmas_card]: boolean | null;
  [AgentKeys.conference_poster]: boolean | null;
  [AgentKeys.login_count]: number | null;
  [AgentKeys.first_login_date]: string | null;
  [AgentKeys.last_login_date]: string | null;
  /** The portal role list. Maps to `portal_roles` in SQL; `roles` is a separate column. */
  [AgentKeys.role]: string[] | null;
  [AgentKeys.roles]: string[] | null;
  [AgentKeys.headshot]: unknown | null;
  [AgentKeys.email_addresses]: PlatformAgentChildDocument[];
  [AgentKeys.addresses]: PlatformAgentChildDocument[];
  [AgentKeys.phone_numbers]: PlatformAgentChildDocument[];
  [AgentKeys.privacyPolicyVersion]: string | null;
  [AgentKeys.privacyPolicyDate]: string | null;
  [AgentKeys.termsOfServicesVersion]: string | null;
  [AgentKeys.termsOfServicesDate]: string | null;
  /**
   * Portal-side fields the index never carried, added for the screens moving off the
   * Firestore document: the auth-stats pivot filters on `logged_in`, the top bar gates
   * its campaign and favourites menus, the splash screen is decided per agent, and the
   * notification centre needs the agent's channels and device tokens.
   *
   * The sub-objects below are passed through key for key, as the child documents are —
   * the consumers that render them already know their shapes. Deliberately absent:
   * anything read only by a whole-document edit form (personal information, sizes,
   * dietary considerations), which stays on Firestore and so needs nothing here.
   */
  [AgentKeys.logged_in]: boolean | null;
  [AgentKeys.showSplashScreen]: boolean | null;
  [AgentKeys.isActiveCampaigns]: boolean | null;
  [AgentKeys.notificationChannels]: string[] | null;
  /** A map of platform id to device token, not a list. */
  [AgentKeys.fcmTokens]: PlatformAgentChildDocument | null;
  /**
   * Whether that map holds anything — the only question asked of it, and the only form of
   * it that can be filtered, since the map itself is jsonb.
   */
  [PlatformAgentDerivedKeys.hasFcmTokens]: boolean | null;
  [AgentKeys.favorites]: unknown | null;
  [AgentKeys.socials]: PlatformAgentChildDocument[] | null;
  [AgentKeys.websites]: PlatformAgentChildDocument[] | null;
  [AgentKeys.personal_goals]: unknown | null;
  [AgentKeys.conference_goals]: unknown | null;
  [AgentKeys.manager_goals]: unknown | null;
  [AgentKeys.tShirtSizes]: unknown | null;
  // Edit-form fields (migration 124).
  [AgentKeys.agent_review_level]: string | null;
  [AgentKeys.approvedBy]: string | null;
  [AgentKeys.certifications]: string | null;
  [AgentKeys.dietaryConsideration]: string | null;
  [AgentKeys.ethnicity]: string | null;
  [AgentKeys.favorite_destination]: string | null;
  [AgentKeys.flightPreference]: string | null;
  [AgentKeys.gender]: string | null;
  [AgentKeys.hobbies]: string | null;
  [AgentKeys.npn]: string | null;
  [AgentKeys.primary_language]: string | null;
  [AgentKeys.prospect_disposition]: string | null;
  [AgentKeys.prospect_priority]: string | null;
  [AgentKeys.prospect_referred_to]: string | null;
  [AgentKeys.prospect_wrap_up_notes]: string | null;
  [AgentKeys.race]: string | null;
  [AgentKeys.registration_source]: string | null;
  [AgentKeys.secondary_language]: string | null;
  [AgentKeys.shoe_size]: string | null;
  [AgentKeys.upline]: string | null;
  [AgentKeys.approvalDate]: string | null;
  [AgentKeys.registrationDate]: string | null;
  [AgentKeys.dob]: string | null;
  [AgentKeys.prospect_referred_to_date]: string | null;
  [AgentKeys.alliance_group_employee]: boolean | null;
  [AgentKeys.emailVerified]: boolean | null;
  [AgentKeys.p_strategic_agent]: boolean | null;
  [AgentKeys.agent_carriers]: unknown | null;
  [AgentKeys.approve_deny_reasons]: unknown | null;
  [AgentKeys.campaigns]: unknown | null;
  [AgentKeys.state_licenses]: unknown | null;
};

/** DevExtreme `LoadOptions`, passed through to the SQL grid engine untouched. */
export type PlatformAgentLoadOptions = Record<string, unknown>;

export enum PlatformAgentSearchRequestKeys {
  loadOptions = 'loadOptions',
}

export interface PlatformAgentSearchRequest {
  [PlatformAgentSearchRequestKeys.loadOptions]: PlatformAgentLoadOptions;
}

export type PlatformAgentSearchResponse = GetListResponse<PlatformAgentRow> & {
  groupCount?: number;
  summary?: unknown[];
};

export enum PlatformAgentsByIdsRequestKeys {
  dbIds = 'dbIds',
}

export interface PlatformAgentsByIdsRequest {
  /** Firestore document ids. */
  [PlatformAgentsByIdsRequestKeys.dbIds]: string[];
}

export enum PlatformAgentByAgentIdRequestKeys {
  agentId = 'agentId',
}

export interface PlatformAgentsByAgentIdRequest {
  /** The business id (`p_agent_id`). */
  [PlatformAgentByAgentIdRequestKeys.agentId]: string;
}

export interface PlatformAgentTeamRequest {
  /** Business id of the agent whose downline is wanted. */
  [PlatformAgentByAgentIdRequestKeys.agentId]: string;
}

export type PlatformAgentListResponse = GetListResponse<PlatformAgentRow>;

export enum PlatformAgentsByEmailRequestKeys {
  emails = 'emails',
}

export interface PlatformAgentsByEmailRequest {
  /**
   * The addresses to resolve. A batch because the callers come in both shapes: a
   * duplicate check asks about one, the conference pages resolve a fixed staff list ten
   * at a time.
   */
  [PlatformAgentsByEmailRequestKeys.emails]: string[];
}

export enum PlatformAgentByEmailKeys {
  email = 'email',
  agents = 'agents',
}

/**
 * The agents holding one requested address.
 *
 * Grouped by the address rather than returned as a flat list because the batch callers
 * need to know which agent answered which question, and matching the flat rows back
 * client-side would mean re-implementing the server's normalization to compare strings.
 *
 * `email` is echoed with its CASING intact — the request is matched case-insensitively,
 * so an echo of the normalized form would hand back a string the caller never sent and
 * could not key on. Surrounding whitespace is the one thing not preserved: the request
 * schema trims it, as every other id-shaped field here does.
 */
export type PlatformAgentByEmailRow = {
  [PlatformAgentByEmailKeys.email]: string;
  [PlatformAgentByEmailKeys.agents]: PlatformAgentRow[];
};

export type PlatformAgentsByEmailResponse = GetListResponse<PlatformAgentByEmailRow>;

export enum PlatformAgentEmailGroupKeys {
  /** The distinct address. Named for the field the grouped view binds as its key. */
  emailAddresses = 'email_addresses',
  agentsCount = 'agentsCount',
  agents = 'agents',
}

/**
 * One distinct email address and the agents holding it.
 *
 * The shape is fixed by the screen that renders it: the grouped-email view binds
 * `email_addresses` as the group key and expands `agents` beneath it, which is what the
 * composite aggregation over the nested email array produced. Keeping the shape means the
 * SQL `GROUP BY` reaches the same template unchanged.
 */
export type PlatformAgentEmailGroupRow = {
  [PlatformAgentEmailGroupKeys.emailAddresses]: string;
  [PlatformAgentEmailGroupKeys.agentsCount]: number;
  [PlatformAgentEmailGroupKeys.agents]: PlatformAgentRow[];
};

export interface PlatformAgentEmailGroupsRequest {
  [PlatformAgentSearchRequestKeys.loadOptions]: PlatformAgentLoadOptions;
}

export type PlatformAgentEmailGroupsResponse = GetListResponse<PlatformAgentEmailGroupRow>;

export enum PlatformAgentExportMetaKeys {
  totalCount = 'totalCount',
  emailsMax = 'emailsMax',
  phoneNumbersMax = 'phoneNumbersMax',
  addressesMax = 'addressesMax',
}

/**
 * The layout parameters of a flat export: how many of each child a single agent in the
 * matched set carries at most, which is how many banded columns the file needs.
 *
 * Computed by the backend over the FILTERED set — the whole point of the export
 * endpoint. The browser used to derive these by scanning every fetched row.
 */
export interface PlatformAgentExportMeta {
  [PlatformAgentExportMetaKeys.totalCount]: number;
  [PlatformAgentExportMetaKeys.emailsMax]: number;
  [PlatformAgentExportMetaKeys.phoneNumbersMax]: number;
  [PlatformAgentExportMetaKeys.addressesMax]: number;
}

export interface PlatformAgentExportRequest {
  [PlatformAgentSearchRequestKeys.loadOptions]: PlatformAgentLoadOptions;
}

/**
 * Everything a client needs to build an export file itself: the matching rows and the
 * column parameters. Deliberately NOT a server-built file — that was tried for the
 * dashboards (`/dashboard/transactions/export`) and abandoned unused; the browser owns
 * layout, styling and the save.
 */
export interface PlatformAgentExportResponse {
  data: PlatformAgentRow[];
  meta: PlatformAgentExportMeta;
}
